import {
  Briefcase,
  Car,
  Clapperboard,
  Dumbbell,
  GraduationCap,
  HeartPulse,
  House,
  type LucideIcon,
  PawPrint,
  Plane,
  ShoppingCart,
  Tag,
  TrendingUp,
  Tv,
  Utensils,
  Wifi,
} from "lucide-react";
import { cn } from "@/lib/utils";

const ICONS = {
  Briefcase,
  Car,
  Clapperboard,
  Dumbbell,
  GraduationCap,
  HeartPulse,
  House,
  PawPrint,
  Plane,
  ShoppingCart,
  Tag,
  TrendingUp,
  Tv,
  Utensils,
  Wifi,
} satisfies Record<string, LucideIcon>;
type IconName = keyof typeof ICONS;

// Palavra-chave (sem acento, minúscula) → ícone. A API ainda não guarda ícone por categoria (D3 da spec).
const RULES: Array<[RegExp, IconName]> = [
  [/mercado|feira|padaria|hortifruti/, "ShoppingCart"],
  [/uber|99|taxi|transporte|combust|gasolina|carro|estacion|onibus|metro/, "Car"],
  [/moradia|aluguel|casa|condom|luz|energia|agua|gas\b|iptu/, "House"],
  [/lazer|cinema|show|teatro|passeio/, "Clapperboard"],
  [/saude|farmac|medic|hospital|dentist|plano/, "HeartPulse"],
  [/restaurante|aliment|comida|ifood|lanche|bar\b/, "Utensils"],
  [/salario|trabalho|freela|renda|pro.?labore/, "Briefcase"],
  [/educa|curso|escola|faculdade|livro/, "GraduationCap"],
  [/internet|telefon|celular/, "Wifi"],
  [/academia|esporte|gym/, "Dumbbell"],
  [/pet|veterin|racao/, "PawPrint"],
  [/viagem|hotel|passagem|aereo/, "Plane"],
  [/assinatura|streaming|netflix|spotify/, "Tv"],
  [/invest|aplicac|poupanca|rendimento/, "TrendingUp"],
];

const TONES = [
  "bg-cat-1/15 text-cat-1",
  "bg-cat-2/15 text-cat-2",
  "bg-cat-3/15 text-cat-3",
  "bg-cat-4/15 text-cat-4",
  "bg-cat-5/15 text-cat-5",
  "bg-cat-6/15 text-cat-6",
  "bg-cat-7/15 text-cat-7",
  "bg-cat-8/15 text-cat-8",
];

const normalize = (s: string) =>
  s
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase();

/** Ícone (por nome) e tom (estável pelo id) de uma categoria — calculados no cliente. */
export function categoryVisual(c: { id: string; name: string }) {
  const name = normalize(c.name);
  const iconName = RULES.find(([re]) => re.test(name))?.[1] ?? "Tag";
  let h = 0;
  for (const ch of c.id) h = (h * 31 + ch.charCodeAt(0)) >>> 0;
  return { iconName, tone: TONES[h % TONES.length] as string };
}

export function CategoryIcon({
  category,
  className,
}: {
  category: { id: string; name: string } | null;
  className?: string;
}) {
  const { iconName, tone } = category
    ? categoryVisual(category)
    : { iconName: "Tag" as const, tone: "bg-surface-2 text-muted" };
  const Icon = ICONS[iconName];
  return (
    <span
      aria-hidden="true"
      className={cn("flex size-10 shrink-0 items-center justify-center rounded-control", tone, className)}
    >
      <Icon className="size-5" />
    </span>
  );
}
